import {useCallback, useEffect, useRef, useState} from "react";
import type {ValuesMap} from "./App.tsx";
import {maxHorizontalAngel, maxVerticalAngel} from "./Sources.tsx";

// Fullscreen icon
const FULLSCREEN_ICON = '⛶';

type SidebarProps = {
    values: ValuesMap;
    isFullscreen: boolean;
    toggleFullscreen: () => void;
};

// minimum swipe distance in pixels to trigger toggle
const SWIPE_THRESHOLD = 50;

// arrow symbols for expand/collapse
const ARROW_DOWN = '▼';
const ARROW_UP = '▲';
const ARROW_RIGHT = '▶';
const ARROW_LEFT = '◀';

/**
 * The sidebar.
 * Divided into three sections from top to bottom:
 * - Instructions: User guidance
 * - Current Audio: Values of the latest selected audio source
 * - Monitoring: User interaction values with the scene
 *
 * Can be collapsed/expanded by the user via button or swipe gesture.
 */
export function Sidebar({values, isFullscreen, toggleFullscreen}: SidebarProps) {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [instructionsExpanded, setInstructionsExpanded] = useState(true);
    const [audioExpanded, setAudioExpanded] = useState(false);
    const [monitoringExpanded, setMonitoringExpanded] = useState(false);
    const touchStartX = useRef<number | null>(null);
    const hasBeenFullscreenRef = useRef(false);

    // Collapse sidebar only on the very first fullscreen entry
    useEffect(() => {
        if (isFullscreen && !hasBeenFullscreenRef.current) {
            setIsCollapsed(true);
            hasBeenFullscreenRef.current = true;
        }
    }, [isFullscreen]);

    // display values in this order
    const sourceOrder = [
        "source", "state", "volume", "x", "y"
    ] as const;
    const sourceValues: Record<string, string> = Object.fromEntries(
        sourceOrder
            .filter((key) => key in values)
            .map((key) => {
                const val = values[key];
                switch (key) {
                    case "source":
                        return [val as string];
                    case "volume":
                        return [key, (val as number).toFixed(0) + " dB"];
                    case "x":
                        return ["horizontal", (val as number * maxHorizontalAngel).toFixed(0) + " °"];
                    case "y":
                        return ["vertical", (val as number * maxVerticalAngel).toFixed(0) + " °"];
                    default:
                        return [key, typeof val === "number" ? val.toFixed(4) : String(val)];
                }
            })
    );

    const monitoringOrder = [
        "hovered", "pointers", "clicked", "action", "direction", "touching"
    ] as const;
    const monitoringValues: Record<string, string> = Object.fromEntries(
        monitoringOrder
            .filter((key) => key in values)
            .map((key) => {
                const val = values[key];
                // numbers as fixed 3 decimal string representation
                switch (key) {
                    case "pointers":
                        return [key, (val as number).toFixed(0)];
                    default:
                        return [key, typeof val === "number" ? val.toFixed(4) : String(val)];
                }
            })
    );

    // swipe gesture handlers
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        if (touchStartX.current === null) return;

        const touchEndX = e.changedTouches[0].clientX;
        const deltaX = touchEndX - touchStartX.current;

        // swipe left on sidebar -> collapse
        if (!isCollapsed && deltaX < -SWIPE_THRESHOLD) {
            setIsCollapsed(true);
        }
        // swipe right on collapsed sidebar -> expand
        if (isCollapsed && deltaX > SWIPE_THRESHOLD) {
            setIsCollapsed(false);
        }

        touchStartX.current = null;
    }, [isCollapsed]);

    return (
        <aside
            className={`sidebar ${isCollapsed ? 'sidebar-collapsed' : ''}`}
            aria-label="display values"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <button
                className="sidebar-toggle"
                onClick={() => setIsCollapsed(!isCollapsed)}
                aria-label={isCollapsed ? 'expand sidebar' : 'collapse sidebar'}
            >
                {isCollapsed ? ARROW_RIGHT : ARROW_LEFT}
            </button>
            <button
                className="fullscreen-button"
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                aria-label={isFullscreen ? 'exit fullscreen' : 'enter fullscreen'}
            >
                {FULLSCREEN_ICON}
            </button>
            {!isCollapsed && (
                <>
                    <div style={{height: '30px', flexShrink: 0}}></div>
                    <div className="sidebar-section">
                        <h2
                            style={{margin: 6, cursor: 'pointer', userSelect: 'none'}}
                            onClick={() => setInstructionsExpanded(!instructionsExpanded)}
                        >
                            <span>Anleitung</span>
                            <span>{instructionsExpanded ? ARROW_UP : ARROW_DOWN}</span>
                        </h2>
                        {instructionsExpanded && (<>
                            <p style={{margin: 6, lineHeight: '1.4'}}>
                                Klicke auf den grünen Würfel, um zu hören, wie du diese Seite bedienen kannst.
                            </p>
                            <p style={{margin: 6, lineHeight: '1.4'}}>
                                Auf einem Smartphone nutze das Querformat.
                            </p>
                        </>)}
                    </div>
                    <div className="sidebar-section">
                        <h2
                            style={{margin: 6, cursor: 'pointer', userSelect: 'none'}}
                            onClick={() => setAudioExpanded(!audioExpanded)}
                        >
                            <span>Audio-Quelle</span>
                            <span>{audioExpanded ? ARROW_UP : ARROW_DOWN}</span>
                        </h2>
                        {audioExpanded && Object.entries(sourceValues).map(([key, val]) => (
                            <div key={key} className="value-row">
                                <span>{key}</span>
                                <strong>{val}</strong>
                            </div>
                        ))}
                    </div>
                    <div className="sidebar-section">
                        <h2
                            style={{margin: 6, cursor: 'pointer', userSelect: 'none'}}
                            onClick={() => setMonitoringExpanded(!monitoringExpanded)}
                        >
                            <span>Monitoring</span>
                            <span>{monitoringExpanded ? ARROW_UP : ARROW_DOWN}</span>
                        </h2>
                        {monitoringExpanded && Object.entries(monitoringValues).map(([key, val]) => (
                            <div key={key} className="value-row">
                                <span>{key}</span>
                                <strong>{val}</strong>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </aside>
    );
}